import type React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  FormControl,
  Autocomplete,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import PasswordInput from "../common/PasswordInput";
import LoadingState from "../common/LoadingState";
import { validatePassword, doPasswordsMatch } from "../../utils/passwordValidation";
import { usePasswordVisibility } from "../../hooks/usePasswordVisibility";
import { CREATE_USER_ROLES } from "../../types/user";
import { TestId } from "../../constants/testIds";

type TranslationKeys = keyof typeof translations.en;

interface CreateUserFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (userData: {
    _id: string;
    name: string;
    role: string;
    password: string;
  }) => Promise<void>;
  error: string | null;
  defaultRole?: string;
}

const USER_ROLES = [...CREATE_USER_ROLES];

const CreateUserForm: React.FC<CreateUserFormProps> = ({
  open,
  onClose,
  onSubmit,
  error,
  defaultRole = "",
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    _id: "",
    name: "",
    role: "",
    password: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { t } = useTranslation();

  const { visibilityStates, toggleVisibility } = usePasswordVisibility({
    initialStates: {
      password: false,
      confirmPassword: false,
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!formData._id.trim()) {
      errors.push("Username is required");
    }

    if (!formData.name.trim()) {
      errors.push("Name is required");
    }

    if (!formData.role) {
      errors.push("Role is required");
    }

    const passwordValidation = validatePassword(formData.password);
    const passwordsMatch = doPasswordsMatch(formData.password, confirmPassword);

    errors.push(...passwordValidation.errors);
    if (!passwordsMatch) {
      errors.push("Passwords do not match");
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSubmit(formData);
      // Let parent component handle closing
    } catch (error) {
      // Error is handled by the parent component
      // Form stays open with error message
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setFormData({ _id: "", name: "", role: defaultRole, password: "" });
      setConfirmPassword("");
      setValidationErrors([]);
    }
  }, [open, defaultRole]);

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <LoadingState message="Creating user..." />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid={TestId.USERS.CREATE_FORM}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t("Create New User" as TranslationKeys)}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" component="div">
                {validationErrors.map((error) => (
                  <div key={error}>{t(error as TranslationKeys)}</div>
                ))}
              </Typography>
            </Alert>
          )}

          <TextField
            autoFocus
            margin="dense"
            label={t("Username" as TranslationKeys)}
            name="_id"
            type="text"
            fullWidth
            value={formData._id}
            onChange={handleChange}
            required
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label={t("Name" as TranslationKeys)}
            name="name"
            type="text"
            fullWidth
            value={formData.name}
            onChange={handleChange}
            required
            sx={{ mb: 2 }}
          />

          <PasswordInput
            label="Temporary Password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            showPassword={visibilityStates.password}
            setShowPassword={() => toggleVisibility("password")}
            disabled={loading}
            sx={{ mb: 2 }}
          />

          <PasswordInput
            label="Confirm Temporary Password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            showPassword={visibilityStates.confirmPassword}
            setShowPassword={() => toggleVisibility("confirmPassword")}
            disabled={loading}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth>
            <Autocomplete
              value={USER_ROLES.find((role) => role === formData.role) || null}
              onChange={(_, newValue) => {
                setFormData((prev) => ({ ...prev, role: newValue || "" }));
              }}
              options={USER_ROLES}
              getOptionLabel={(option) => t(option as TranslationKeys)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Role" as TranslationKeys)}
                  required
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": TestId.USERS.ROLE_SELECT,
                  }}
                />
              )}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("Cancel" as TranslationKeys)}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              loading ||
              !formData._id ||
              !formData.name ||
              !formData.role ||
              !formData.password ||
              !confirmPassword
            }
          >
            {loading ? <CircularProgress size={24} /> : t("Add User" as TranslationKeys)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateUserForm;
